import {
  BodyText,
  ControlledSelect,
  FieldSecondaryLabel,
  FieldsetCard,
  Icon,
  renderSelectMenuItems,
  useConfirmDialog,
  useFormContainerContext,
} from '@if/mui'
import { Grid, IconButton, Stack } from '@mui/material'
import { EProtectionPlanCoverage } from '@src/types/CalculatorEstimateDto'
import { PersonalLoanCalculatorSchemaType } from '@src/types/PersonalLoanCalculatorSchema'
import { Info } from 'lucide-react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface ProtectionPlanDisplayProps {
  /**
   * False when the selected term can't legally carry insurance
   * (`effectiveRateWithInsuranceIsLegal`), same gate as the worksheet.
   */
  supportsInsurance: boolean
}

const COVERAGE_CHOICES = [
  { value: EProtectionPlanCoverage.None, labelKey: 'calculator.protectionPlanNone' },
  { value: EProtectionPlanCoverage.Single, labelKey: 'calculator.protectionPlanSingle' },
  { value: EProtectionPlanCoverage.Joint, labelKey: 'calculator.protectionPlanJoint' },
] as const

/** Three-way rather than a checkbox: joint prices at the co-applicant rate, so it moves the payment. */
export const ProtectionPlanDisplay = ({ supportsInsurance }: ProtectionPlanDisplayProps) => {
  const { t } = useTranslation()
  const {
    methods: { control },
  } = useFormContainerContext<PersonalLoanCalculatorSchemaType>()

  const insuranceInfoDialog = useConfirmDialog({
    mode: 'info',
    title: t('worksheet.addProtectionPlan'),
    confirmLabel: t('common.close'),
    hideCancel: true,
    message: (
      <Stack spacing={2} sx={{ textAlign: 'justify' }}>
        <BodyText>{t('worksheet.includeProtectionPlan')}</BodyText>
        <BodyText>{t('worksheet.insuranceIncentive')}</BodyText>
        <BodyText>{t('worksheet.lifeInsurance')}</BodyText>
        <BodyText>{t('worksheet.accidentalDisability')}</BodyText>
      </Stack>
    ),
  })

  const coverageOptions = useMemo(
    () => renderSelectMenuItems(COVERAGE_CHOICES, (choice) => ({ value: choice.value, label: t(choice.labelKey) })),
    [t],
  )

  // FieldsetCard `title` node trick: the prop is typed
  // as `string` but renders any node.
  const title = (
    <>
      {t('calculator.protectionPlanTitle')} <FieldSecondaryLabel>{t('calculator.optional')}</FieldSecondaryLabel>
    </>
  ) as unknown as string

  return (
    <FieldsetCard
      title={title}
      subtitle={
        supportsInsurance ? t('calculator.protectionPlanDescription') : t('calculator.protectionPlanUnavailable')
      }
    >
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, md: 5 }}>
          <ControlledSelect
            name="protectionPlan"
            control={control}
            label={t('calculator.protectionPlanCoverage')}
            options={coverageOptions}
            disabled={!supportsInsurance}
            disableEmptyOption
            fullWidth
          />
        </Grid>
        <Grid>
          <IconButton onClick={() => insuranceInfoDialog.open()} aria-label={t('worksheet.includeProtectionPlan')}>
            <Icon icon={Info} />
          </IconButton>
        </Grid>
      </Grid>
    </FieldsetCard>
  )
}
